import config from "@Config/index";
import axios from "axios";

export interface CommunicationPreferences {
  newsletters: {
    email: boolean;
    whatsapp: boolean;
  };
  promos_offers: {
    email: boolean;
    whatsapp: boolean;
  };
  bookings_updates: {
    email: boolean;
    whatsapp: boolean;
    sms: boolean;
  };
  app_notifications: {
    enabled: boolean;
  };
}

// API returns preferences directly, not wrapped in a response object
export type CommunicationPreferencesResponse = CommunicationPreferences;

// API returns preferences directly for updates as well
export type UpdatePreferencesResponse = CommunicationPreferences;

/**
 * Get user communication preferences
 * @returns User communication preferences
 */
export const getCommunicationPreferences = async (
  userAuthToken: string,
): Promise<CommunicationPreferencesResponse> => {
  const response = await axios.get(
    `${config.API_URL}/api/v0/auth/users/communication_preferences`,
    {
      params: {
        ...(userAuthToken && { user_auth_token: userAuthToken }),
      },
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.data;
};

/**
 * Update all communication preferences
 * @param preferences - Complete preferences object
 * @returns Updated preferences
 */
export const updateAllCommunicationPreferences = async (
  preferences: CommunicationPreferences,
  userAuthToken: string,
): Promise<UpdatePreferencesResponse> => {
  const { data } = await axios.put(
    `${config.API_URL}/api/v0/auth/users/communication_preferences`,
    {
      preferences,
    },
    {
      params: {
        ...(userAuthToken && { user_auth_token: userAuthToken }),
      },
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return data;
};

/**
 * Update a single communication preference
 * @param category - Category (newsletters, promos_offers, bookings_updates, mode_of_contact)
 * @param channel - Channel (email, whatsapp, sms, call)
 * @param enabled - Whether the preference is enabled
 * @returns Updated preferences
 */
export const updateSingleCommunicationPreference = async (
  category: keyof CommunicationPreferences,
  channel: string,
  enabled: boolean,
  userAuthToken: string,
): Promise<UpdatePreferencesResponse> => {
  const { data } = await axios.patch(
    `${config.API_URL}/api/v0/auth/users/communication_preferences/partial`,
    {
      category,
      channel,
      enabled,
    },
    {
      params: {
        ...(userAuthToken && { user_auth_token: userAuthToken }),
      },
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return data;
};

/**
 * Unsubscribe from all non-essential communications
 * @returns Updated preferences
 */
export const unsubscribeFromAll = async (
  userAuthToken: string,
): Promise<UpdatePreferencesResponse> => {
  const { data } = await axios.post(
    `${config.API_URL}/api/v0/auth/users/communication_preferences/unsubscribe_all`,
    {},
    {
      params: {
        ...(userAuthToken && { user_auth_token: userAuthToken }),
      },
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return data;
};
